import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { usePermissions } from '../../hooks/usePermissions';
import { getOAuthConnectUrl, triggerSync } from '../../store/slices/googleReviewSlice';
import {
  canTriggerSync,
  deriveSyncBarState,
  formatCountdown,
} from '../../utils/googleReviewSync';

/**
 * The single home for all sync UX (GOOGLE_REVIEW_MANAGER.md §6). One bar above the filters,
 * present in every state, driven entirely by sync_state.
 */
const SyncStatus: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const syncState = useAppSelector((s) => s.googleReview.syncState);
  const isSyncTriggering = useAppSelector((s) => s.googleReview.isSyncTriggering);
  // A sync sweep covers the whole account (all locations) in one job, so surface the count on the
  // CTA to make that scope obvious. 0/1 locations → generic label (no misleading "1 location").
  const locationCount = useAppSelector((s) => s.googleReview.locations.length);
  // Subuser: triggering a sync / (re)connecting Google requires google_review_write.
  const { can } = usePermissions();
  const canWrite = can('google_review_write');
  const [isConnecting, setIsConnecting] = useState(false);

  // Re-render once a minute so the "available in Xh Ym" countdown stays current.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => forceTick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const barState = deriveSyncBarState(syncState);
  const canSync = canTriggerSync(syncState);
  const countdown = formatCountdown(syncState?.nextSyncAvailableAt);

  const localTime = (iso: string | null | undefined): string =>
    iso ? new Date(iso).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }) : '';

  const handleSync = () => {
    if (!canSync || isSyncTriggering) return;
    dispatch(triggerSync());
  };

  const handleReconnect = async () => {
    setIsConnecting(true);
    try {
      const result = await dispatch(getOAuthConnectUrl()).unwrap() as { url: string };
      if (result.url && result.url !== '#mock-oauth') {
        window.location.href = result.url;
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const pill = {
    never_synced: { label: t('google_review_sync_pill_never'), cls: 'is-neutral' },
    in_progress: { label: t('google_review_sync_pill_in_progress'), cls: 'is-progress' },
    synced: { label: t('google_review_sync_pill_synced'), cls: 'is-synced' },
    failed: { label: t('google_review_sync_pill_failed'), cls: 'is-failed' },
    needs_reconnect: { label: t('google_review_sync_pill_reconnect'), cls: 'is-failed' },
  }[barState];

  const { headline, subline } = ((): { headline: string; subline: string } => {
    switch (barState) {
      case 'in_progress':
        return {
          headline: t('google_review_sync_in_progress_title'),
          subline: syncState?.lastSyncAt
            ? t('google_review_sync_started_sub')
            : t('google_review_sync_in_progress_sub'),
        };
      case 'synced':
        return {
          headline: t('google_review_sync_last_synced', { time: localTime(syncState?.lastSyncAt) }),
          subline: countdown
            ? t('google_review_sync_next_available', { countdown })
            : t('google_review_sync_up_to_date'),
        };
      case 'failed': {
        // Global (project-wide) Google quota is not this user's fault — show a distinct
        // "temporarily busy" message instead of the generic failure. Value mirrors
        // GoogleReviewErrorCodeEnum::ApiQuotaExceeded on the backend.
        if (syncState?.lastError === 'google_api_quota_exceeded') {
          return {
            headline: t('google_review_sync_busy_title'),
            subline: t('google_review_sync_busy_sub'),
          };
        }
        return {
          headline: t('google_review_sync_failed_title'),
          subline: syncState?.lastSyncAt
            ? t('google_review_sync_failed_has_reviews', { time: localTime(syncState?.lastSyncAt) })
            : t('google_review_sync_failed_first'),
        };
      }
      case 'needs_reconnect':
        return {
          headline: t('google_review_sync_reconnect_title'),
          subline: t('google_review_sync_reconnect_sub'),
        };
      default:
        return {
          headline: t('google_review_sync_never_title'),
          subline: t('google_review_sync_never_sub'),
        };
    }
  })();

  const renderButton = () => {
    if (barState === 'needs_reconnect') {
      return (
        <button
          type="button"
          className="google-review-sync__btn google-review-sync__btn--reconnect"
          onClick={handleReconnect}
          disabled={isConnecting || !canWrite}
          title={!canWrite ? t('subuser_no_permission_tooltip') : undefined}
        >
          {isConnecting ? t('loading_text') : t('google_review_sync_reconnect_btn')}
        </button>
      );
    }

    if (barState === 'in_progress') {
      return (
        <button type="button" className="google-review-sync__btn" disabled>
          <span className="google-review-sync__spinner" aria-hidden="true" />
          {t('google_review_sync_syncing_btn')}
        </button>
      );
    }

    const label =
      locationCount > 1
        ? t('google_review_sync_btn_count', { count: locationCount })
        : barState === 'failed'
          ? t('google_review_sync_retry_btn')
          : t('google_review_sync_start_btn');

    return (
      <div className="google-review-sync__action">
        <button
          type="button"
          className="google-review-sync__btn"
          onClick={handleSync}
          disabled={!canSync || isSyncTriggering || !canWrite}
          title={!canWrite ? t('subuser_no_permission_tooltip') : undefined}
        >
          {label}
        </button>
        {!canSync && countdown && (
          <span className="google-review-sync__countdown">
            {t('google_review_sync_available_in', { countdown })}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={`google-review-sync google-review-sync--${barState}`} role="status">
      <div className="google-review-sync__info">
        <div className="google-review-sync__headline-row">
          <span className="google-review-sync__headline">{headline}</span>
          <span className={`google-review-sync__pill ${pill.cls}`}>{pill.label}</span>
        </div>
        <span className="google-review-sync__subline">{subline}</span>
      </div>
      {renderButton()}
    </div>
  );
};

export default SyncStatus;
